/**
 * AuthContext - Google Sign-In Authentication Context
 * 
 * Manages authentication state for case editing functionality.
 * Uses Google Identity Services for sign-in.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// Google OAuth Client ID from environment
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [idToken, setIdToken] = useState(null);

    // Initialize Google Identity Services
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) {
            console.warn('GOOGLE_CLIENT_ID not configured - authentication disabled');
            setIsLoading(false);
            return;
        }

        // Load the Google Identity Services script
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeGoogleAuth;
        document.body.appendChild(script);

        return () => {
            // Cleanup
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, []);

    const initializeGoogleAuth = useCallback(() => {
        if (!window.google) {
            console.error('Google Identity Services not loaded');
            setIsLoading(false);
            return;
        }

        try {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleCredentialResponse,
                auto_select: false,
                cancel_on_tap_outside: true,
            });
            setIsLoading(false);
        } catch (err) {
            console.error('Error initializing Google Auth:', err);
            setError('Failed to initialize authentication');
            setIsLoading(false);
        }
    }, []);

    const handleCredentialResponse = async (response) => {
        try {
            const initialToken = response.credential;

            // Verify token with backend
            const verifyResponse = await fetch('/api/auth/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: initialToken }),
            });

            if (!verifyResponse.ok) {
                throw new Error('Token verification failed');
            }

            const data = await verifyResponse.json();
            if (data.authenticated && data.user) {
                setUser(data.user);
                setError(null);

                // Use the long-lived App Token returned by backend
                const appToken = data.token || initialToken;
                setIdToken(appToken);

                // Store token in localStorage for persistence (survives tab close)
                localStorage.setItem('authToken', appToken);
            } else {
                throw new Error('Authentication failed');
            }
        } catch (err) {
            console.error('Authentication error:', err);
            setError(err.message);
            setUser(null);
            setIdToken(null);
        }
    };

    const signIn = useCallback(() => {
        if (!window.google || !GOOGLE_CLIENT_ID) {
            setError('Google Sign-In not available');
            return;
        }

        try {
            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed()) {
                    // Fallback: render the button if prompt doesn't show
                    console.log('One-tap not displayed, using button fallback');
                }
            });
        } catch (err) {
            console.error('Error triggering sign-in:', err);
            setError('Failed to start sign-in');
        }
    }, []);

    const signOut = useCallback(() => {
        setUser(null);
        setIdToken(null);
        localStorage.removeItem('authToken');

        if (window.google) {
            window.google.accounts.id.disableAutoSelect();
        }
    }, []);

    // Automatic token refresh logic removed as we now use long-lived App Tokens (4 hours)
    // The previous refresh logic using Google One Tap was unreliable and causing timeouts.
    // The backend now issues a 4-hour token upon successful Google Sign-In.

    // Check for existing session on mount
    useEffect(() => {
        const checkStoredToken = async () => {
            const storedToken = localStorage.getItem('authToken');
            if (storedToken) {
                console.log('Found stored authToken, verifying with backend...');
                try {
                    // Verify directly with backend
                    const verifyResponse = await fetch('/api/auth/verify', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token: storedToken }),
                    });

                    if (!verifyResponse.ok) {
                        throw new Error('Token verification failed');
                    }

                    const data = await verifyResponse.json();
                    if (data.authenticated && data.user) {
                        console.log('Stored token verified successfully. User:', data.user.email);
                        setUser(data.user);
                        setError(null);

                        // Update with fresh token if provided
                        const appToken = data.token || storedToken;
                        setIdToken(appToken);
                        localStorage.setItem('authToken', appToken);
                    } else {
                        console.warn('Stored token invalid');
                        localStorage.removeItem('authToken');
                    }
                } catch (err) {
                    console.error('Error verifying stored token:', err);
                    localStorage.removeItem('authToken');
                }
            }
        };

        checkStoredToken();
    }, []);

    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        idToken,
        signIn,
        signOut,
        isConfigured: !!GOOGLE_CLIENT_ID,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
